import React, { useMemo, useRef } from "react";
import { Group, Text } from "react-konva";
import Konva from "konva";
import { MondayItemColumn } from "../../controller";
import { FONT_SIZE, FONT_FAMILY, PADDING } from "../../consts";
import { measureTextWidth } from "../../text-utils";
import { getColumnDisplayValue } from "../../utils";
import { KonvaTooltip } from "frontend/ui-components/design-system/tooltip/konva-tooltip";
import { MONDAY_TASK_WIDTH } from "shared/datamodel/monday-task";

interface ColumnDetailRowProps {
  column: MondayItemColumn;
  x: number;
  y: number;
}

const TITLE_COLOR = "#7F869B";
const VALUE_COLOR = "#1E1E1E";
const LETTER_SPACING = 0.1824;
const TEXT_WIDTH = MONDAY_TASK_WIDTH - PADDING * 2;
const GAP = 16;

export const ColumnDetailRow = React.memo(({ column, x, y }: ColumnDetailRowProps) => {
  const displayValue = getColumnDisplayValue(column);
  const tooltipNode = useRef<Konva.Text | null>(null);

  // Debug for case where title was undefined
  if (!column.title) {
    console.error("Column title not found:", column);
  }

  const { isTruncated } = useMemo(() => {
    return measureTextWidth(displayValue, {
      fontSize: FONT_SIZE,
      fontFamily: FONT_FAMILY,
      fontStyle: "normal",
      maxWidth: TEXT_WIDTH,
    });
  }, [displayValue]);

  return (
    <>
      <Group x={x} y={y}>
        <Text
          width={TEXT_WIDTH}
          text={`${column.title}:`}
          fill={TITLE_COLOR}
          fontFamily={FONT_FAMILY}
          fontSize={FONT_SIZE}
          letterSpacing={LETTER_SPACING}
          wrap="none"
          ellipsis
        />

        <Text
          ref={tooltipNode}
          y={GAP}
          text={displayValue}
          fill={VALUE_COLOR}
          fontFamily={FONT_FAMILY}
          fontSize={FONT_SIZE}
          letterSpacing={LETTER_SPACING}
          width={TEXT_WIDTH}
          ellipsis
          wrap="none"
        />
      </Group>

      <KonvaTooltip text={displayValue} nodeRef={tooltipNode} offset={8} maxWidth={512} disabled={!isTruncated} />
    </>
  );
});

ColumnDetailRow.displayName = "ColumnDetailRow";
